'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetPositionsRow, positionEntityQueryKey, useGetPositions } from '@morfi/backend-sdk-v2/client';
import { useFormatter, useTranslations } from 'next-intl';
import { Skeleton } from '@/components/ui/app-skeleton';
import { SelectField, type SelectFieldItem } from '@/components/ui/select-field';
import { D6_SCALE, USDC_DUST_THRESHOLD_D6 } from '@/config/solana';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { estimateOutcomeTokenSpotValueFromStringsD6 } from '@/lib/gifts/outcome-token-spot-value';

/**
 * Single outcome holding row used by the asset picker. The shape comes from
 * the grouped-holdings handler (see backend `holdings.ts`).
 */
export interface OutcomeHoldingChoice {
  marketPubkey: string;
  marketTitle: string | null;
  outcomeIndex: number;
  outcomeLabel: string | null;
  /** Outcome token balance (D6 microunits). */
  balanceD6: string;
  /** Current price (D9 integer): value_d6 = balance_d6 × price_d9 / 1e9. */
  currentPriceD9: string;
}
interface OutcomeTokenPickerProps {
  walletAddress: string;
  selectedKey: string | null;
  onChange: (choice: OutcomeHoldingChoice | null) => void;
}
function makeKey(c: {
  marketPubkey: string;
  outcomeIndex: number;
}): string {
  return `${c.marketPubkey}__${c.outcomeIndex}`;
}

/**
 * Displays the connected wallet's non-dust outcome-token holdings as a
 * `<SelectField>` dropdown. Filters out closed (resolved/cancelled) markets —
 * gifting only makes sense for live, tradable positions. When the user has no
 * holdings, renders an empty-state message with a CTA back to the markets list.
 *
 * Uses `<SelectField>` (the same Radix Select primitive as the "From wallet"
 * picker one field above) rather than a Popover: inside the gift `SilkSheet`,
 * the Popover trigger never opened (`aria-expanded` stayed false), so the
 * outcome could not be selected and the flow was stuck (MOR-678). The Select
 * primitive opens reliably in the same modal.
 */
export function OutcomeTokenPicker(props) {
  const $ = _c(39);
  const t = useTranslations("gift.send");
  const format = useFormatter();
  const {
    formatD6
  } = useFormattedCurrency();
  let t0;
  if ($[0] !== props.walletAddress) {
    t0 = [props.walletAddress];
    $[0] = props.walletAddress;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["market", "marketTitle", "marketStatus", "outcomeIndex", "outcomeLabel", "balance", "currentPriceD9"];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props.walletAddress || $[4] !== t0) {
    t2 = {
      userId: props.walletAddress,
      walletPubkeys: t0,
      includeZero: "true",
      fields: t1
    };
    $[3] = props.walletAddress;
    $[4] = t0;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const query = useGetPositions(t2);
  const rows = useLiveListData(query.queryKey, query.rows, positionEntityQueryKey);
  const {
    isPending
  } = query;
  if (isPending) {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Skeleton className="h-10 w-full" />;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let choices;
  let t3;
  let t4;
  if ($[7] !== format || $[8] !== formatD6 || $[9] !== rows || $[10] !== t) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      choices = rows.flatMap(_temp);
      if (choices.length === 0) {
        let t5;
        if ($[14] !== t) {
          t5 = t("asset.noHoldings");
          $[14] = t;
          $[15] = t5;
        } else {
          t5 = $[15];
        }
        let t6;
        if ($[16] !== t5) {
          t6 = <p className="text-caption text-foreground-secondary">{t5}</p>;
          $[16] = t5;
          $[17] = t6;
        } else {
          t6 = $[17];
        }
        let t7;
        if ($[18] !== t) {
          t7 = t("asset.tradeFirstCta");
          $[18] = t;
          $[19] = t7;
        } else {
          t7 = $[19];
        }
        let t8;
        if ($[20] !== t7) {
          t8 = <Link href="/markets" className="text-caption font-medium text-foreground-primary hover:underline">{t7}</Link>;
          $[20] = t7;
          $[21] = t8;
        } else {
          t8 = $[21];
        }
        let t9;
        if ($[22] !== t6 || $[23] !== t8) {
          t9 = <div className="flex flex-col gap-2 rounded-control border border-dashed border-border-primary bg-background-tertiary/40 p-4 text-center">{t6}{t8}</div>;
          $[22] = t6;
          $[23] = t8;
          $[24] = t9;
        } else {
          t9 = $[24];
        }
        t4 = t9;
        break bb0;
      }
      let t5;
      if ($[25] !== format || $[26] !== formatD6 || $[27] !== t) {
        t5 = c => {
          const valueD6 = estimateOutcomeTokenSpotValueFromStringsD6(c.balanceD6, c.currentPriceD9);
          if (valueD6 === null) {
            return {
              value: makeKey(c),
              label: c.outcomeLabel ?? `Outcome ${c.outcomeIndex}`
            };
          }
          const outcome = c.outcomeLabel !== null && c.outcomeLabel.trim() !== "" ? c.outcomeLabel : `Outcome ${c.outcomeIndex}`;
          const titleDisplay = c.marketTitle ?? c.marketPubkey.slice(0, 8);
          const tokensDisplay = format.number(Number(c.balanceD6) / D6_SCALE, {
            maximumFractionDigits: 2
          });
          return {
            value: makeKey(c),
            label: <span className="flex w-full min-w-0 items-center justify-between gap-2"><span className="min-w-0 truncate">{t("asset.marketLabel", {
                  title: titleDisplay,
                  outcome
                })}</span><span className="shrink-0 text-caption text-foreground-secondary">{t("asset.holdingSummary", {
                  tokens: tokensDisplay,
                  value: formatD6(valueD6.toString())
                })}</span></span>
          };
        };
        $[25] = format;
        $[26] = formatD6;
        $[27] = t;
        $[28] = t5;
      } else {
        t5 = $[28];
      }
      t3 = choices.map(t5);
    }
    $[7] = format;
    $[8] = formatD6;
    $[9] = rows;
    $[10] = t;
    $[11] = choices;
    $[12] = t3;
    $[13] = t4;
  } else {
    choices = $[11];
    t3 = $[12];
    t4 = $[13];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  const items = t3;
  const t5 = props.selectedKey ?? undefined;
  let t6;
  if ($[29] !== choices || $[30] !== props) {
    t6 = key => {
      props.onChange(choices.find(c_0 => makeKey(c_0) === key) ?? null);
    };
    $[29] = choices;
    $[30] = props;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== t) {
    t7 = t("asset.selectPlaceholder");
    $[32] = t;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== items || $[35] !== t5 || $[36] !== t6 || $[37] !== t7) {
    t8 = <SelectField value={t5} onValueChange={t6} items={items} placeholder={t7} fullWidth={true} />;
    $[34] = items;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  return t8;
}
function _temp(r) {
  if (r.marketStatus !== 2) {
    return [];
  }
  const spotValueD6 = estimateOutcomeTokenSpotValueFromStringsD6(r.balance, r.currentPriceD9);
  if (spotValueD6 === null || spotValueD6 < BigInt(USDC_DUST_THRESHOLD_D6)) {
    return [];
  }
  return [{
    marketPubkey: r.market,
    marketTitle: r.marketTitle,
    outcomeIndex: r.outcomeIndex,
    outcomeLabel: r.outcomeLabel,
    balanceD6: r.balance,
    currentPriceD9: r.currentPriceD9
  }];
}